import {
  fetchHydra as baseFetchHydra,
  hydraDataProvider as baseHydraDataProvider
} from "@api-platform/admin";
import { parseHydraDocumentation } from "@api-platform/api-doc-parser";

const getHeaders = () => localStorage.getItem("token") ? {
  Authorization: `Bearer ${localStorage.getItem("token")}`,
} : {};

const fetchHydra = (url, options = {}) =>
  baseFetchHydra(url, {
    ...options,
    headers: getHeaders,
  });

const apiDocumentationParser = (setRedirectToLogin) => async () => {
  try {
    setRedirectToLogin(false);
    return await parseHydraDocumentation(process.env.REACT_APP_ENTRYPOINT, { headers: getHeaders });
  } catch (result) {
    const { api, response, status } = result;
    if (status !== 401 || !response) {
      throw result;
    }

    // Prevent infinite loop if the token is expired
    localStorage.removeItem("token");

    setRedirectToLogin(true);

    return {
      api,
      response,
      status,
    };
  }
};

// Embedded documents (kept as objects in records because of `useEmbedded: true`)
// must be sent back to the API as IRIs: API Platform rejects nested documents on write.
const isEmbeddedDocument = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  !(value instanceof File) &&
  typeof value["@id"] === "string";

const embeddedToIri = (value) => {
  if (isEmbeddedDocument(value)) return value["@id"];
  if (Array.isArray(value)) return value.map(embeddedToIri);
  return value;
};

const serializeData = (data = {}) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, embeddedToIri(value)])
  );

const dataProvider = (setRedirectToLogin) => {
  const baseDataProvider = baseHydraDataProvider({
    entrypoint: process.env.REACT_APP_ENTRYPOINT,
    httpClient: fetchHydra,
    apiDocumentationParser: apiDocumentationParser(setRedirectToLogin),
    useEmbedded: true,
  });

  return {
    ...baseDataProvider,
    create: (resource, params) =>
      baseDataProvider.create(resource, { ...params, data: serializeData(params.data) }),
    update: (resource, params) =>
      baseDataProvider.update(resource, { ...params, data: serializeData(params.data) }),
    updateMany: (resource, params) =>
      baseDataProvider.updateMany(resource, { ...params, data: serializeData(params.data) }),
  };
};

export default dataProvider;